import { lazy } from "react";
import * as Sentry from "@sentry/react";

/**
 * Wraps a lazy import with retry logic for handling stale chunks after deploys.
 *
 * Strategy:
 *   - On first failure, retry the original import up to 3 times with exponential backoff.
 *   - We do NOT use cache-busted URLs (e.g. ?t=Date.now()) because that can create a
 *     separate module evaluation context, leading to duplicate React instances and
 *     "Invalid hook call" errors.
 *   - On final failure, classify the error. If it looks like a stale-bundle ChunkLoadError
 *     (the diagnostic signature for "old tab requesting a hashed chunk that no longer
 *     exists on the CDN"), trigger the Phase 1 blocking modal via
 *     window.__forceVersionBlockingModal__() if available, then hard-reload as a
 *     last-resort safety net so the tab cannot get stuck on a white screen.
 *   - A one-shot, per-path sessionStorage guard prevents a reload LOOP: if a chunk
 *     load fails AGAIN on the same path after we already reloaded once, we stop
 *     reloading and throw to the error boundary instead.
 *
 * Telemetry is split by outcome so a recovered stale-tab chunk load (expected churn
 * after a deploy) does not drown out genuine, actionable lazy-load failures:
 *   - first chunk failure (about to recover via reload) → Sentry.captureMessage
 *     (level "warning", stable fingerprint chunk-load-stale-tab-recovered)
 *   - second chunk failure on the same path (unrecovered) → Sentry.captureException
 *     (reloadAlreadyAttempted: true), then throw to the boundary
 *   - non-chunk failure (genuine bug) → Sentry.captureException
 *
 * See RCA: /Users/admin/Desktop/2026-06-08-stale-tab-version-check-rca.md
 */

// Sentry signatures we have observed in prod:
//   - ChunkLoadError: Loading chunk 123 failed       (webpack-style)
//   - TypeError: Failed to fetch dynamically imported module: <hash>.js  (Vite, Chrome/Edge)
//   - TypeError: error loading dynamically imported module               (Vite, Firefox)
//   - TypeError: Importing a module script failed                        (Vite, Safari)
//   - Issue JAVASCRIPT-REACT-NM matches the first Vite/Chrome variant.
const VITE_DYNAMIC_IMPORT_FAILURE_PATTERN =
    /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk \S+ failed/i;

const isChunkLoadError = (error) => {
    if (!error) return false;
    const name = error.name || "";
    const message = error.message || "";

    // Direct ChunkLoadError (webpack) or Vite-style TypeError with any of the
    // dynamic-import failure messages above. We also accept a bare match on
    // the message even when the error name is missing or mangled by minifiers
    // — the message text is the most reliable signal.
    if (name === "ChunkLoadError") return true;
    if (name === "TypeError" && VITE_DYNAMIC_IMPORT_FAILURE_PATTERN.test(message)) return true;
    if (VITE_DYNAMIC_IMPORT_FAILURE_PATTERN.test(message)) return true;

    return false;
};

const CHUNK_RELOAD_GUARD_PREFIX = "lazy-chunk-reload-attempted";

const getChunkReloadGuardKey = () => {
    if (typeof window === "undefined") return `${CHUNK_RELOAD_GUARD_PREFIX}:server`;
    return `${CHUNK_RELOAD_GUARD_PREFIX}:${window.location?.pathname || "/"}`;
};

const getSessionStorage = () => {
    if (typeof window === "undefined") return null;
    try {
        return window.sessionStorage;
    } catch {
        return null;
    }
};

const clearChunkReloadGuard = () => {
    const storage = getSessionStorage();
    if (!storage) return;
    try {
        storage.removeItem(getChunkReloadGuardKey());
    } catch {
        // Ignore storage failures; recovery must not depend on storage support.
    }
};

const hasAttemptedChunkReload = () => {
    const storage = getSessionStorage();
    if (!storage) return false;
    try {
        return storage.getItem(getChunkReloadGuardKey()) === "true";
    } catch {
        return false;
    }
};

const markChunkReloadAttempted = () => {
    const storage = getSessionStorage();
    if (!storage) return;
    try {
        storage.setItem(getChunkReloadGuardKey(), "true");
    } catch {
        // Ignore storage failures; we still attempt the reload.
    }
};

export const loadWithRetries = async (
    importer,
    {
        maxRetries = 3,
        retryBaseDelay = 1000,
        reloadDelay = 1500,
        // Late-bound so the default never touches `window` at evaluation time
        // (the inline `() => window.location.reload()` form would throw under SSR
        // even when this code path is never actually reached).
        reloadPage = undefined,
    } = {}
) => {
    const doReload =
        reloadPage ||
        (() => {
            if (typeof window !== "undefined") window.location.reload();
        });
    for (let attempt = 0; attempt <= maxRetries; attempt += 1) {
        try {
            // eslint-disable-next-line no-await-in-loop
            const loadedModule = await importer();
            clearChunkReloadGuard();
            return loadedModule;
        } catch (error) {
            if (attempt === maxRetries) {
                const chunkError = isChunkLoadError(error);

                // Read the guard ONCE, before we mark it below. We reuse this value
                // for BOTH the telemetry decision and the reload decision — calling
                // hasAttemptedChunkReload() twice with a mark in between would
                // misclassify the first failure as "already reloaded".
                const alreadyReloaded = chunkError ? hasAttemptedChunkReload() : false;

                // Log to Sentry FIRST so we never lose the signal — even if the
                // forced reload below races us. Split by outcome:
                //   - recovered chunk load (first failure) → low-severity warning
                //     with a stable fingerprint so it doesn't drown out real bugs
                //   - unrecovered chunk load (second failure on same path) → exception
                //   - genuine non-chunk failure → exception
                try {
                    if (chunkError && !alreadyReloaded) {
                        // A recovered stale-tab chunk load is expected churn after a
                        // deploy, not an actionable bug — report it as a
                        // lower-severity warning with a stable fingerprint so it
                        // doesn't drown out genuine lazy-load failures.
                        Sentry.captureMessage("Stale-tab chunk load recovered (reloading)", {
                            level: "warning",
                            tags: {
                                chunk_load_error: "true",
                                source: "lazyWithRetries",
                                recovered: "true",
                            },
                            fingerprint: ["chunk-load-stale-tab-recovered"],
                            extra: {
                                retries: maxRetries,
                                originalMessage: error?.message,
                                route:
                                    typeof window !== "undefined"
                                        ? window.location?.pathname
                                        : undefined,
                                userAgent:
                                    typeof navigator !== "undefined" ? navigator.userAgent : "n/a",
                                reloadGuardKey: getChunkReloadGuardKey(),
                            },
                        });
                    } else {
                        // Either a genuine non-chunk failure, or a chunk failure that
                        // PERSISTED after we already reloaded once (the reload didn't
                        // fix it) — both are visible/actionable, so use captureException.
                        Sentry.captureException(error, {
                            tags: {
                                source: "lazyWithRetries",
                                chunk_load_error: chunkError ? "true" : "false",
                            },
                            extra: {
                                retries: maxRetries,
                                userAgent:
                                    typeof navigator !== "undefined" ? navigator.userAgent : "n/a",
                                reloadGuardKey: getChunkReloadGuardKey(),
                                reloadAlreadyAttempted: alreadyReloaded,
                            },
                        });
                    }
                } catch {
                    // Swallow Sentry failure — don't let logging block recovery.
                }

                if (chunkError && typeof window !== "undefined") {
                    // One-shot guard: if we already reloaded once for this path and
                    // the chunk STILL fails, the reload didn't fix it. Stop looping
                    // and throw to the error boundary instead of reloading forever.
                    if (alreadyReloaded) {
                        throw error;
                    }

                    markChunkReloadAttempted();

                    try {
                        // Surface the Phase 1 blocking modal. The user has hit the
                        // most painful version-skew failure (404 on a hashed chunk);
                        // the modal makes the next click "Refresh now" not "white
                        // screen, abandon the workflow".
                        if (typeof window.__forceVersionBlockingModal__ === "function") {
                            window.__forceVersionBlockingModal__();
                            // Give the modal a beat to mount before falling back
                            // to a hard reload. If the user clicks Refresh before
                            // the timer fires, refreshApp() will reload us anyway.
                            // eslint-disable-next-line no-await-in-loop
                            return await new Promise((resolve) => {
                                setTimeout(() => {
                                    doReload();
                                    // Never-resolve so React doesn't try to render.
                                    resolve(new Promise(() => {}));
                                }, reloadDelay);
                            });
                        }
                    } catch {
                        // Fall through to hard reload.
                    }

                    console.error(
                        "[lazyWithRetries] Lazy load failed after retries. Reloading page.",
                        error
                    );
                    doReload();
                    // Never-resolving promise — prevent React from attempting to render.
                    return new Promise(() => {});
                }

                throw error;
            }
            // Exponential backoff: 1s, 2s, 4s
            const delay = retryBaseDelay * (2 ** attempt);
            // eslint-disable-next-line no-await-in-loop
            await new Promise((resolve) => setTimeout(resolve, delay));
        }
    }
    // Unreachable — the loop either returns, reloads, or throws.
    return undefined;
};

export const lazyWithRetries = (importer) => lazy(() => loadWithRetries(importer));

export const __lazyRetryTestUtils = {
    isChunkLoadError,
    getChunkReloadGuardKey,
    clearChunkReloadGuard,
};
